import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth, getIdToken } from '@pooflabs/web';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyLuckyDrawPrizes } from '@/lib/collections/luckyDrawPrizes';
import type { LuckyDrawPrizesResponse as _LuckyDrawPrizesResponse } from '@/lib/collections/luckyDrawPrizes';
import { subscribeLuckyDrawCampaigns } from '@/lib/collections/luckyDrawCampaigns';
import type { LuckyDrawCampaignsResponse } from '@/lib/collections/luckyDrawCampaigns';
import { playBoing, playPop, playClick } from '@/utils/sounds';
import { toast } from 'sonner';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';

// Extend with imageUrl which is written to the DB but not yet in the generated type
type LuckyDrawPrizesResponse = _LuckyDrawPrizesResponse & { imageUrl?: string };

// ─── Types ───────────────────────────────────────────────────────────────────

type DrawState = 'idle' | 'drawing' | 'won' | 'lost' | 'already_drew' | 'campaign_ended' | 'max_wins_reached';

interface DrawResult {
  won: boolean;
  prizeType: string;
  entryId?: string;
}

// ─── Prize Config ─────────────────────────────────────────────────────────────

type ReelPrize = { key: string; emoji: string; label: string; color: string; imageUrl?: string };

// Default fallback prizes (used before API data loads)
const DEFAULT_REEL_PRIZES: ReelPrize[] = [
  { key: 'freeNFT', emoji: '🎨', label: 'Free Mint NFT', color: '#7CFC00' },
  { key: 'customNFT', emoji: '✨', label: 'Custom NFT', color: '#FFD700' },
  { key: 'merch', emoji: '👕', label: 'Merch Voucher', color: '#FF6B9D' },
  { key: 'blindBox', emoji: '📦', label: 'Blind Box', color: '#9B59B6' },
];

// Default colors cycling for dynamically added prizes
const DEFAULT_COLORS = ['#7CFC00', '#FFD700', '#FF6B9D', '#9B59B6', '#1DA1F2', '#FF8C00', '#E63946'];

const PRIZE_EMOJI: Record<string, string> = {
  freeNFT: '🎨',
  customNFT: '✨',
  merch: '👕',
  blindBox: '📦',
  none: '💀',
};

const PRIZE_COLOR: Record<string, string> = {
  freeNFT: '#7CFC00',
  customNFT: '#FFD700',
  merch: '#FF6B9D',
  blindBox: '#9B59B6',
  none: '#E63946',
};

// Tile height in px
const TILE_H = 100;
// How many full loops of the prizes before landing
const LOOP_COUNT = 6;

// Build the strip order: repeat prizes LOOP_COUNT times
function buildStrip(reelPrizes: ReelPrize[]): ReelPrize[] {
  const strip: ReelPrize[] = [];
  for (let i = 0; i < LOOP_COUNT; i++) {
    for (const p of reelPrizes) strip.push(p);
  }
  return strip;
}

// localStorage keys
const LS_LAST_DRAW_TS = 'dangerdan_lucky_draw_last_ts';
const LS_WIN_ENTRY_ID = 'dangerdan_win_entry_id';
const LS_WIN_PRIZE_TYPE = 'dangerdan_win_prize_type';

// ─── Countdown Timer ─────────────────────────────────────────────────────────

const useCountdown = (targetTs: number | null) => {
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (!targetTs) return;
    const tick = () => {
      const diff = targetTs * 1000 - Date.now();
      setRemaining(Math.max(0, Math.floor(diff / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetTs]);

  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  return { hours, minutes, seconds, remaining };
};

// ─── Single Reel ─────────────────────────────────────────────────────────────

interface ReelProps {
  targetIndex: number; // which prize index to land on
  spinning: boolean;
  prizeImageMap?: Record<string, string>; // prizeKey -> imageUrl
  stopped: boolean;
  spinKey: number; // increment to restart animation
  reelPrizes: ReelPrize[]; // dynamic prize list
}

const Reel: React.FC<ReelProps> = ({ targetIndex, spinning, stopped, spinKey, prizeImageMap = {}, reelPrizes }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const positionRef = useRef<number>(0);

  const REEL_STRIP = useMemo(() => buildStrip(reelPrizes), [reelPrizes]);

  // The strip is STRIP_LENGTH tiles tall. We want to land so the middle visible
  // slot shows the target prize. Middle slot = tile at index 1 in a 3-visible window.
  // Final translateY (negative) = -(targetStripIndex * TILE_H - TILE_H)
  // We pick the last occurrence of targetIndex in the strip (near end = more scroll distance)
  const lastOccurrence = (() => {
    const targetKey = reelPrizes[targetIndex]?.key;
    if (!targetKey) return targetIndex;
    for (let i = REEL_STRIP.length - 1; i >= 0; i--) {
      if (REEL_STRIP[i].key === targetKey) return i;
    }
    return targetIndex;
  })();
  // Center the middle row on this tile: offset by 1 tile (show tile above, target, tile below)
  const finalY = -((lastOccurrence - 1) * TILE_H);

  useEffect(() => {
    if (!spinning && !stopped) {
      // Reset to top
      if (containerRef.current) {
        containerRef.current.style.transition = 'none';
        containerRef.current.style.transform = 'translateY(0px)';
      }
      positionRef.current = 0;
      return;
    }

    if (spinning && !stopped) {
      // Rapid scroll loop using requestAnimationFrame
      let lastTs = 0;
      startTimeRef.current = performance.now();

      const animate = (ts: number) => {
        if (!containerRef.current) return;
        // Speed: 800px/s during spin
        const speed = 800;
        const delta = ts - lastTs;
        lastTs = ts;

        positionRef.current -= (speed * delta) / 1000;

        // Wrap: when we go past the full strip height, reset to top
        const stripH = REEL_STRIP.length * TILE_H;
        if (Math.abs(positionRef.current) >= stripH) {
          positionRef.current += stripH;
        }

        containerRef.current.style.transition = 'none';
        containerRef.current.style.transform = `translateY(${positionRef.current}px)`;

        animFrameRef.current = requestAnimationFrame(animate);
      };
      animFrameRef.current = requestAnimationFrame(animate);

      return () => {
        cancelAnimationFrame(animFrameRef.current);
      };
    }

    if (stopped) {
      // Stop the animation loop
      cancelAnimationFrame(animFrameRef.current);

      // Ease-out to final position
      if (containerRef.current) {
        containerRef.current.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        containerRef.current.style.transform = `translateY(${finalY}px)`;
      }
    }
  }, [spinning, stopped, finalY, spinKey]);

  return (
    <div
      className="arcade-reel"
      style={{
        width: '110px',
        height: `${TILE_H * 3}px`,
        overflow: 'hidden',
        position: 'relative',
        borderRadius: '4px',
        background: '#050a18',
        boxShadow: 'inset 0 0 20px rgba(0,0,0,0.8), inset 0 2px 4px rgba(0,0,0,0.9)',
      }}
    >
      {/* Gradient masks top/bottom */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg, rgba(5,10,24,0.92) 0%, transparent 22%, transparent 78%, rgba(5,10,24,0.92) 100%)',
        }}
      />
      {/* Center row highlight line */}
      <div
        aria-hidden="true"
        className="reel-center-highlight"
        style={{
          position: 'absolute',
          top: TILE_H,
          left: 0,
          right: 0,
          height: TILE_H,
          zIndex: 2,
          pointerEvents: 'none',
          border: '2px solid rgba(124,252,0,0.5)',
          boxShadow: 'inset 0 0 16px rgba(124,252,0,0.12)',
        }}
      />
      {/* Strip */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          willChange: 'transform',
        }}
      >
        {REEL_STRIP.map((prize, i) => (
          <div
            key={i}
            style={{
              width: '100%',
              height: TILE_H,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
            }}
          >
            {prizeImageMap[prize.key] ? (
              <img
                src={prizeImageMap[prize.key]}
                alt={prize.label}
                style={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 4 }}
              />
            ) : (
              <span style={{ fontSize: '38px', lineHeight: 1 }}>{prize.emoji}</span>
            )}
            <span
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '0.6rem',
                letterSpacing: '0.08em',
                color: prize.color,
                textAlign: 'center',
                lineHeight: 1.1,
              }}
            >
              {prize.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Info Popup Modal ─────────────────────────────────────────────────────────

interface InfoPopupProps {
  open: boolean;
  onClose: () => void;
  drawsToday: number;
  hours: number;
  minutes: number;
  seconds: number;
  prizes: LuckyDrawPrizesResponse[];
  wonPrizeType?: string;
  walletAddress?: string;
}

const InfoPopup: React.FC<InfoPopupProps> = ({
  open,
  onClose,
  drawsToday,
  hours,
  minutes,
  seconds,
  prizes,
  wonPrizeType,
  walletAddress,
}) => {
  // Close on Escape key and lock body scroll
  useEffect(() => {
    if (!open) return;
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [open, onClose]);

  if (!open) return null;

  const walletShort = walletAddress
    ? `${walletAddress.slice(0, 4)}…${walletAddress.slice(-4)}`
    : null;

  return (
    <div
      onClick={onClose}
      onTouchMove={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)',
        animation: 'popup-bg-in 0.18s ease-out',
        overflow: 'hidden',
        overscrollBehavior: 'contain',
      }}
    >
      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: 'linear-gradient(160deg, #0c1228 0%, #080d1f 100%)',
          border: '4px solid #7CFC00',
          borderRadius: '12px',
          boxShadow: '0 0 50px rgba(124,252,0,0.22), 0 0 100px rgba(124,252,0,0.07), 8px 8px 0 rgba(0,0,0,0.95)',
          width: '100%',
          maxWidth: '420px',
          maxHeight: 'min(85vh, 85dvh)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          animation: 'popup-card-in 0.22s cubic-bezier(0.175,0.885,0.32,1.275)',
        }}
      >
        {/* Header */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'linear-gradient(160deg, #0c1228 0%, #080d1f 100%)',
            borderBottom: '2px solid rgba(124,252,0,0.25)',
            padding: '16px 20px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.5rem',
              letterSpacing: '0.2em',
              color: '#7CFC00',
              textShadow: '0 0 16px rgba(124,252,0,0.6), 3px 3px 0 rgba(0,0,0,0.9)',
            }}
          >
            STATUS &amp; INFO
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.2rem',
              letterSpacing: '0.06em',
              color: '#fff',
              background: '#E63946',
              border: '3px solid rgba(0,0,0,0.8)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.8)',
              borderRadius: '6px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
              transition: 'transform 0.12s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1) rotate(-3deg)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1) rotate(0)'; }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Status rows */}
          <div>
            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '0.75rem',
                letterSpacing: '0.22em',
                color: 'rgba(124,252,0,0.55)',
                marginBottom: '8px',
              }}
            >
              ★ YOUR STATUS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {/* Wallet */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${walletShort ? 'rgba(124,252,0,0.2)' : 'rgba(255,255,255,0.07)'}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.6rem',
                    color: 'rgba(255,255,255,0.3)',
                    letterSpacing: '0.1em',
                  }}
                >
                  WALLET
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontSize: '9px', color: walletShort ? '#7CFC00' : '#E63946' }}>
                    {walletShort ? '●' : '○'}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Bangers', cursive",
                      fontSize: '0.8rem',
                      letterSpacing: '0.06em',
                      color: walletShort ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.35)',
                    }}
                  >
                    {walletShort ?? 'Not connected'}
                  </span>
                </div>
              </div>
              {/* Draws today */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${drawsToday === 0 ? 'rgba(124,252,0,0.2)' : 'rgba(255,100,0,0.25)'}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.6rem',
                    color: 'rgba(255,255,255,0.3)',
                    letterSpacing: '0.1em',
                  }}
                >
                  DRAWS TODAY
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontSize: '9px', color: drawsToday === 0 ? '#7CFC00' : '#FF6B00' }}>
                    {drawsToday === 0 ? '●' : '○'}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Bangers', cursive",
                      fontSize: '0.8rem',
                      letterSpacing: '0.06em',
                      color: drawsToday === 0 ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)',
                    }}
                  >
                    {drawsToday === 0 ? '1 Available' : 'Used (0/1)'}
                  </span>
                </div>
              </div>
            </div>

            {/* Countdown if drew today */}
            {drawsToday > 0 && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: 'rgba(124,252,0,0.04)',
                  border: '1px solid rgba(124,252,0,0.15)',
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.58rem',
                    color: 'rgba(255,255,255,0.3)',
                    letterSpacing: '0.12em',
                  }}
                >
                  NEXT DRAW IN
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {[
                    { val: hours, label: 'HRS' },
                    { val: minutes, label: 'MIN' },
                    { val: seconds, label: 'SEC' },
                  ].map(({ val, label }, i) => (
                    <React.Fragment key={label}>
                      {i > 0 && (
                        <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1.1rem', color: 'rgba(124,252,0,0.4)' }}>:</span>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                        <div
                          style={{
                            fontFamily: "'Bangers', cursive",
                            fontSize: '1.2rem',
                            letterSpacing: '0.06em',
                            color: '#7CFC00',
                            lineHeight: 1,
                            minWidth: '36px',
                            textAlign: 'center',
                            background: 'rgba(124,252,0,0.07)',
                            border: '1px solid rgba(124,252,0,0.2)',
                            padding: '2px 4px',
                          }}
                        >
                          {String(val).padStart(2, '0')}
                        </div>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.5rem',
                            color: 'rgba(255,255,255,0.3)',
                            letterSpacing: '0.1em',
                          }}
                        >
                          {label}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: '1px', background: 'rgba(124,252,0,0.15)', margin: '0 -4px' }} />

          {/* How to play */}
          <div>
            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '0.75rem',
                letterSpacing: '0.22em',
                color: 'rgba(124,252,0,0.55)',
                marginBottom: '8px',
              }}
            >
              ★ HOW TO PLAY
            </div>
            <div
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '6px',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {[
                { step: '1', icon: '👛', text: 'Connect your wallet' },
                { step: '2', icon: '🎰', text: 'Hit SPIN — one free draw per day' },
                { step: '3', icon: '🏆', text: 'Win prizes — your connected wallet is auto-claimed!' },
              ].map(({ step, icon, text }) => (
                <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div
                    style={{
                      fontFamily: "'Bangers', cursive",
                      fontSize: '0.9rem',
                      letterSpacing: '0.08em',
                      color: '#080d1f',
                      background: '#7CFC00',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: '2px solid rgba(0,0,0,0.5)',
                    }}
                  >
                    {step}
                  </div>
                  <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
                  <span
                    style={{
                      fontFamily: "'Bangers', cursive",
                      fontSize: '0.82rem',
                      letterSpacing: '0.06em',
                      color: 'rgba(255,255,255,0.75)',
                      lineHeight: 1.2,
                    }}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          {prizes.length > 0 && (
            <div style={{ height: '1px', background: 'rgba(124,252,0,0.15)', margin: '0 -4px' }} />
          )}

          {/* Prize list */}
          {prizes.length > 0 && (
            <div>
              <div
                style={{
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.75rem',
                  letterSpacing: '0.22em',
                  color: 'rgba(124,252,0,0.55)',
                  marginBottom: '8px',
                }}
              >
                ★ PRIZE POOL
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {prizes.map((prize) => {
                  const emoji = PRIZE_EMOJI[prize.prizeType] ?? '🎁';
                  const color = PRIZE_COLOR[prize.prizeType] ?? '#7CFC00';
                  const pct = prize.totalCount > 0 ? prize.remainingCount / prize.totalCount : 0;
                  const isSoldOut = prize.remainingCount <= 0;
                  const isWon = wonPrizeType === prize.prizeType;

                  return (
                    <div
                      key={prize.id}
                      style={{
                        background: isWon
                          ? `rgba(${hexToRgb(color)},0.12)`
                          : 'rgba(255,255,255,0.02)',
                        border: isWon
                          ? `2px solid ${color}`
                          : '1px solid rgba(255,255,255,0.07)',
                        borderRadius: '5px',
                        padding: '9px 12px',
                        opacity: isSoldOut ? 0.4 : 1,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        {prize.imageUrl ? (
                          <img src={prize.imageUrl} alt={prize.label} style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} />
                        ) : (
                          <span style={{ fontSize: '20px', flexShrink: 0 }}>{emoji}</span>
                        )}
                        <span
                          style={{
                            fontFamily: "'Bangers', cursive",
                            fontSize: '0.9rem',
                            letterSpacing: '0.07em',
                            color: isSoldOut ? 'rgba(255,255,255,0.25)' : '#fff',
                            lineHeight: 1.1,
                            flex: 1,
                          }}
                        >
                          {prize.label}
                        </span>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.62rem',
                            color: isSoldOut ? 'rgba(255,255,255,0.2)' : `${color}bb`,
                            flexShrink: 0,
                          }}
                        >
                          {prize.remainingCount}/{prize.totalCount}
                        </span>
                      </div>
                      <div
                        style={{
                          height: '3px',
                          background: 'rgba(255,255,255,0.07)',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${pct * 100}%`,
                            background: isSoldOut ? '#333' : color,
                            borderRadius: '2px',
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── Slot Machine Cabinet ─────────────────────────────────────────────────────

interface SlotMachineProps {
  reelStates: { targetIndex: number; spinning: boolean; stopped: boolean }[];
  onSpin: () => void;
  spinDisabled: boolean;
  spinKey: number;
  spinLabel: string;
  effectiveState: DrawState;
  isCamingSoon?: boolean;
  campaignStartAt?: number;
  hours: number;
  minutes: number;
  seconds: number;
  showWinOverlay: boolean;
  showLoseOverlay: boolean;
  winPrizeType: string;
  winPrizeLabel: string;
  winPrizeImageUrl?: string;
  walletConnected: boolean;
  onConnectWallet: () => void;
  onDisconnectWallet: () => void;
  prizeImageMap: Record<string, string>;
  reelPrizes: ReelPrize[];
  onOpenInfo: () => void;
}

const SlotMachine: React.FC<SlotMachineProps> = ({
  reelStates,
  onSpin,
  spinDisabled,
  spinKey,
  spinLabel,
  effectiveState,
  isCamingSoon = false,
  campaignStartAt,
  hours,
  minutes,
  seconds,
  showWinOverlay,
  showLoseOverlay,
  winPrizeType,
  winPrizeLabel,
  winPrizeImageUrl,
  walletConnected,
  onConnectWallet,
  onDisconnectWallet,
  prizeImageMap,
  reelPrizes,
  onOpenInfo,
}) => {
  const [btnHovered, setBtnHovered] = useState(false);
  const winColor = PRIZE_COLOR[winPrizeType] ?? '#7CFC00';

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '460px',
      }}
    >
      {/* Cabinet outer shell */}
      <div
        className="arcade-cabinet"
        style={{
          position: 'relative',
          background: 'linear-gradient(160deg, #0e1530 0%, #080d1f 60%, #050b16 100%)',
          border: '6px solid #7CFC00',
          borderRadius: '16px',
          padding: '0 0 28px',
          boxShadow:
            '0 0 0 4px rgba(124,252,0,0.18), 0 0 60px rgba(124,252,0,0.22), 0 0 120px rgba(124,252,0,0.08), 8px 8px 0 rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04)',
          width: '100%',
          maxWidth: '460px',
          overflow: 'hidden',
        }}
      >
        {/* LED side strips */}
        <div
          aria-hidden="true"
          className="led-strip-left"
          style={{
            position: 'absolute',
            top: '10px',
            left: 0,
            width: '8px',
            bottom: 0,
            zIndex: 5,
            pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden="true"
          className="led-strip-right"
          style={{
            position: 'absolute',
            top: '10px',
            right: 0,
            width: '8px',
            bottom: 0,
            zIndex: 5,
            pointerEvents: 'none',
          }}
        />

        {/* Corner neon diamonds — inside cabinet bounds since overflow:hidden */}
        {([
          { pos: 'top-left',     style: { top: '14px', left: '14px' }   },
          { pos: 'top-right',    style: { top: '14px', right: '14px' }  },
          { pos: 'bottom-left',  style: { bottom: '14px', left: '14px' } },
          { pos: 'bottom-right', style: { bottom: '14px', right: '14px' } },
        ] as const).map(({ pos }, idx) => (
          <div
            key={pos}
            aria-hidden="true"
            className="neon-corner"
            style={{
              position: 'absolute',
              ...([
                { top: '14px', left: '14px' },
                { top: '14px', right: '14px' },
                { bottom: '14px', left: '14px' },
                { bottom: '14px', right: '14px' },
              ][idx]),
              fontSize: '22px',
              lineHeight: 1,
              zIndex: 15,
              animationDelay: `${idx * 0.8}s`,
              color: ['#7CFC00','#E63946','#FFD700','#7CFC00'][idx],
            }}
          >
            ◆
          </div>
        ))}

        {/* ? Info button — direct child of cabinet so position:absolute resolves to cabinet, above overflow:hidden clipping */}
        <button
          onClick={onOpenInfo}
          aria-label="Info & Status"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 20,
            fontFamily: "'Bangers', cursive",
            fontSize: '1rem',
            letterSpacing: '0.04em',
            color: '#7CFC00',
            background: '#080d1f',
            border: '2px solid #7CFC00',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
            boxShadow: '0 0 10px rgba(124,252,0,0.3), 2px 2px 0 rgba(0,0,0,0.8)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
          }}
          onMouseEnter={(e) => {
            const b = e.currentTarget as HTMLButtonElement;
            b.style.transform = 'scale(1.15) rotate(-5deg)';
            b.style.boxShadow = '0 0 18px rgba(124,252,0,0.6), 2px 2px 0 rgba(0,0,0,0.8)';
            b.style.background = 'rgba(124,252,0,0.12)';
          }}
          onMouseLeave={(e) => {
            const b = e.currentTarget as HTMLButtonElement;
            b.style.transform = 'scale(1) rotate(0)';
            b.style.boxShadow = '0 0 10px rgba(124,252,0,0.3), 2px 2px 0 rgba(0,0,0,0.8)';
            b.style.background = '#080d1f';
          }}
        >
          ?
        </button>

        {/* Inner content wrapper with padding (accounts for LED strips) */}
        <div className="arcade-cabinet-inner" style={{ padding: '16px 28px 0', position: 'relative', zIndex: 10 }}>

        {/* Title marquee */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div
            className="cabinet-title-neon"
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(2rem, 6vw, 2.8rem)',
              letterSpacing: '0.18em',
              color: '#00BFFF',
              textShadow:
                '0 0 20px rgba(0,191,255,0.9), 0 0 50px rgba(0,191,255,0.5), 0 0 80px rgba(0,191,255,0.2), 3px 3px 0 rgba(0,0,0,0.9)',
              lineHeight: 0.95,
            }}
          >
            FEELING LUCKY?
          </div>
          <div
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '0.72rem',
              letterSpacing: '0.22em',
              color: 'rgba(124,252,0,0.55)',
              marginTop: '4px',
            }}
          >
            ★ ONE DRAW PER DAY ★
          </div>
        </div>

        {/* Chrome reel frame */}
        <div
          style={{
            position: 'relative',
            background:
              'linear-gradient(180deg, #141428 0%, #0a0a1a 100%)',
            border: '5px solid rgba(255,255,255,0.18)',
            borderRadius: '10px',
            padding: '14px 16px',
            boxShadow:
              'inset 0 2px 12px rgba(0,0,0,0.9), inset 0 -1px 3px rgba(255,255,255,0.06), 0 0 0 2px rgba(0,0,0,0.6), 0 0 20px rgba(0,0,0,0.6)',
          }}
        >
          {/* Chrome inner bevel */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 2,
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.05)',
              pointerEvents: 'none',
            }}
          />

          {/* 3 Reels */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {reelStates.map((reel, i) => (
              <React.Fragment key={i}>
                <Reel
                  targetIndex={reel.targetIndex}
                  spinning={reel.spinning}
                  stopped={reel.stopped}
                  spinKey={spinKey}
                  prizeImageMap={prizeImageMap}
                  reelPrizes={reelPrizes}
                />
                {i < 2 && (
                  <div
                    aria-hidden="true"
                    style={{
                      width: '4px',
                      height: '40px',
                      background:
                        'linear-gradient(180deg, transparent, rgba(124,252,0,0.3), transparent)',
                      borderRadius: '2px',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Scanline effect */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '6px',
              background:
                'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px)',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* SPIN button */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            className="arcade-spin-btn"
            onClick={onSpin}
            disabled={spinDisabled}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.6rem, 5vw, 2.4rem)',
              letterSpacing: '0.14em',
              color: spinDisabled ? 'rgba(255,255,255,0.3)' : '#080d1f',
              background: spinDisabled
                ? 'rgba(255,255,255,0.06)'
                : btnHovered
                ? '#9dff1a'
                : '#7CFC00',
              border: `4px solid ${spinDisabled ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.8)'}`,
              borderRadius: '8px',
              boxShadow: spinDisabled
                ? 'none'
                : btnHovered
                ? '6px 6px 0 rgba(0,0,0,0.9), 0 0 30px rgba(124,252,0,0.5)'
                : '5px 5px 0 rgba(0,0,0,0.85)',
              padding: '10px 48px',
              cursor: spinDisabled ? 'not-allowed' : 'pointer',
              transform: spinDisabled
                ? 'none'
                : btnHovered
                ? 'translateY(-3px) scale(1.04)'
                : 'translateY(0) scale(1)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {spinLabel}
          </button>
        </div>

        {/* Status messages below button */}
        <div style={{ marginTop: '14px', minHeight: '60px' }}>
          {/* No wallet — show connect wallet CTA */}
          {effectiveState !== 'campaign_ended' && !walletConnected && (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={onConnectWallet}
                style={{
                  fontFamily: "'Bangers', cursive",
                  fontSize: '1rem',
                  letterSpacing: '0.1em',
                  color: '#080d1f',
                  background: '#7CFC00',
                  border: '3px solid rgba(0,0,0,0.8)',
                  boxShadow: '3px 3px 0 rgba(0,0,0,0.8)',
                  padding: '6px 22px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '4px',
                  transition: 'transform 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05) translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1) translateY(0)';
                }}
              >
                Connect Wallet
              </button>
            </div>
          )}

          {/* Wallet connected — show disconnect button */}
          {effectiveState !== 'campaign_ended' && walletConnected && (
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <button
                onClick={onDisconnectWallet}
                style={{
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.8rem',
                  letterSpacing: '0.1em',
                  color: '#E63946',
                  background: 'rgba(8,13,31,0.9)',
                  border: '2px solid rgba(230,57,70,0.3)',
                  borderRadius: '4px',
                  padding: '4px 14px',
                  cursor: 'pointer',
                  transition: 'transform 0.12s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                }}
              >
                DISCONNECT WALLET
              </button>
            </div>
          )}

          {/* Campaign ended / coming soon */}
          {effectiveState === 'campaign_ended' && (
            <div
              style={{
                textAlign: 'center',
                fontFamily: "'Bangers', cursive",
                fontSize: '1.1rem',
                letterSpacing: '0.14em',
                color: isCamingSoon ? '#F59E0B' : '#E63946',
              }}
            >
              {isCamingSoon
                ? `⏳ COMING SOON — Opens ${campaignStartAt ? new Date(campaignStartAt * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'soon'}`
                : '🏁 DRAW ENDED'}
            </div>
          )}

          {/* Already drew today — show countdown */}
          {(effectiveState === 'already_drew') && (
            <CountdownDisplay hours={hours} minutes={minutes} seconds={seconds} compact />
          )}

          {/* Max wins reached */}
          {effectiveState === 'max_wins_reached' && (
            <div style={{
              textAlign: 'center',
              fontFamily: "'Bangers', cursive",
              fontSize: '1rem',
              letterSpacing: '0.1em',
              color: '#7CFC00',
              lineHeight: 1.3,
            }}>
              You've already won 3 prizes!<br />
              <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                Thanks for playing 🎉
              </span>
            </div>
          )}
        </div>

        {/* Win overlay */}
        {showWinOverlay && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: `radial-gradient(circle at 50% 50%, ${winColor}22 0%, rgba(8,13,31,0.97) 70%)`,
              animation: 'sm-win-flash 0.4s ease-out',
              zIndex: 10,
            }}
          >
            {/* Confetti burst */}
            <ConfettiBurst color={winColor} />

            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
                letterSpacing: '0.1em',
                color: winColor,
                textShadow: `0 0 30px ${winColor}88, 4px 4px 0 rgba(0,0,0,0.9)`,
                animation: 'sm-jackpot-in 0.5s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
              }}
            >
              JACKPOT! 🎰
            </div>
            <div
              style={{
                fontSize: '3rem',
                margin: '8px 0',
                animation: 'sm-prize-drop 0.5s 0.15s cubic-bezier(0.175,0.885,0.32,1.275) both',
              }}
            >
              {winPrizeImageUrl ? (
                <img src={winPrizeImageUrl} alt={winPrizeLabel} style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                PRIZE_EMOJI[winPrizeType] ?? '🎁'
              )}
            </div>
            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '1.4rem',
                letterSpacing: '0.14em',
                color: '#fff',
                background: winColor,
                padding: '4px 20px',
                border: '3px solid rgba(0,0,0,0.8)',
                boxShadow: '3px 3px 0 rgba(0,0,0,0.8)',
                animation: 'sm-label-up 0.4s 0.25s ease-out both',
              }}
            >
              {winPrizeLabel}
            </div>
          </div>
        )}

        {/* Lose overlay */}
        {showLoseOverlay && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(8,13,31,0.96)',
              animation: 'sm-lose-shake 0.5s ease',
              zIndex: 10,
            }}
          >
            <div style={{ fontSize: '3.5rem', marginBottom: '8px' }}>💀</div>
            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(1.4rem, 5vw, 2rem)',
                letterSpacing: '0.12em',
                color: '#E63946',
                textShadow: '3px 3px 0 rgba(0,0,0,0.9)',
                textAlign: 'center',
                padding: '0 20px',
              }}
            >
              NO LUCK...
            </div>
            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '0.85rem',
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.35)',
                marginTop: '4px',
              }}
            >
              TRY AGAIN IN 24H
            </div>
          </div>
        )}
        </div>{/* end inner content wrapper */}

      </div>
    </div>
  );
};

// ─── Confetti Burst ───────────────────────────────────────────────────────────

const ConfettiBurst: React.FC<{ color: string }> = ({ color }) => {
  const particles = Array.from({ length: 14 }, (_, i) => ({
    angle: (360 / 14) * i,
    size: 6 + Math.random() * 6,
    delay: Math.random() * 0.2,
    colors: [color, '#FFD700', '#FF6B9D', '#7CFC00'],
  }));

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        borderRadius: '10px',
      }}
    >
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: p.size,
            height: p.size,
            background: p.colors[i % p.colors.length],
            borderRadius: i % 3 === 0 ? '50%' : '2px',
            animation: `sm-confetti-${i % 4} 0.8s ${p.delay}s ease-out both`,
            transformOrigin: '0 0',
          }}
        />
      ))}
    </div>
  );
};

// ─── Countdown Display ────────────────────────────────────────────────────────

const CountdownDisplay: React.FC<{
  hours: number;
  minutes: number;
  seconds: number;
  compact?: boolean;
}> = ({ hours, minutes, seconds, compact }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: compact ? '4px' : '8px' }}>
    <div
      style={{
        fontFamily: 'monospace',
        fontSize: compact ? '0.6rem' : '0.75rem',
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.12em',
      }}
    >
      NEXT DRAW IN
    </div>
    <div style={{ display: 'flex', gap: compact ? '4px' : '8px', alignItems: 'center' }}>
      {[
        { val: hours, label: 'HRS' },
        { val: minutes, label: 'MIN' },
        { val: seconds, label: 'SEC' },
      ].map(({ val, label }, i) => (
        <React.Fragment key={label}>
          {i > 0 && (
            <span
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: compact ? '1rem' : '1.4rem',
                color: 'rgba(124,252,0,0.4)',
              }}
            >
              :
            </span>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <div
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: compact ? '1.1rem' : 'clamp(1.6rem,5vw,2.4rem)',
                letterSpacing: '0.06em',
                color: '#7CFC00',
                lineHeight: 1,
                minWidth: compact ? '34px' : '52px',
                textAlign: 'center',
                background: 'rgba(124,252,0,0.07)',
                border: '1px solid rgba(124,252,0,0.2)',
                padding: compact ? '2px 4px' : '4px 8px',
              }}
            >
              {String(val).padStart(2, '0')}
            </div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '0.55rem',
                color: 'rgba(255,255,255,0.3)',
                letterSpacing: '0.12em',
              }}
            >
              {label}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

// WalletClaimForm removed — prize claiming is now handled by admin manually after winner shares on X.

// ─── Win Flow ─────────────────────────────────────────────────────────────────
// Step 1: Share on X button. Step 2: Confirmation message after sharing.

function isMintOrVoucherPrize(prizeType: string, label: string): boolean {
  const combined = `${prizeType} ${label}`.toLowerCase();
  return combined.includes('mint') || combined.includes('voucher') || combined.includes('free');
}

const WinShareFlow: React.FC<{
  prizeType: string;
  prizeName: string;
}> = ({ prizeType, prizeName }) => {
  const [shared, setShared] = useState(false);

  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://dangerdan.net';
  const tweetText = `🎰 I just won ${prizeName} from @dangerdan_nft Lucky Draw on Solana!\n\nEnter now 👉 ${pageUrl}`;

  const handleShare = () => {
    playBoing();
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`,
      '_blank',
      'noopener,noreferrer'
    );
    setShared(true);
  };

  const isMintOrVoucher = isMintOrVoucherPrize(prizeType, prizeName);

  if (shared) {
    return (
      <div
        style={{
          marginTop: '12px',
          padding: '18px 20px',
          background: 'rgba(124,252,0,0.07)',
          border: '2px solid rgba(124,252,0,0.35)',
          borderRadius: '8px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: '1.15rem',
            letterSpacing: '0.1em',
            color: '#7CFC00',
            marginBottom: '8px',
            textShadow: '0 0 12px rgba(124,252,0,0.4)',
          }}
        >
          {isMintOrVoucher
            ? '🎟️ YOUR FREE MINT / VOUCHER HAS BEEN RECORDED!'
            : '🎁 CONGRATS!'}
        </div>
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.55)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {isMintOrVoucher
            ? 'Our team will reach out with your prize details.'
            : 'Admin will transfer your prize to your wallet shortly. Keep an eye on your wallet!'}
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={handleShare}
      style={{
        marginTop: '12px',
        width: '100%',
        fontFamily: "'Bangers', cursive",
        fontSize: '1.3rem',
        letterSpacing: '0.14em',
        color: '#fff',
        background: '#000',
        border: '3px solid rgba(255,255,255,0.2)',
        boxShadow: '0 0 20px rgba(255,255,255,0.08), 4px 4px 0 rgba(0,0,0,0.9)',
        borderRadius: '6px',
        padding: '12px 16px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        transition: 'transform 0.12s ease, background 0.12s ease, box-shadow 0.12s ease',
        animation: 'share-btn-pulse 1.8s ease-in-out infinite',
      }}
      onMouseEnter={(e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        btn.style.transform = 'translateY(-3px) scale(1.02)';
        btn.style.background = '#111';
        btn.style.boxShadow = '0 0 30px rgba(255,255,255,0.15), 5px 5px 0 rgba(0,0,0,0.9)';
      }}
      onMouseLeave={(e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        btn.style.transform = 'translateY(0) scale(1)';
        btn.style.background = '#000';
        btn.style.boxShadow = '0 0 20px rgba(255,255,255,0.08), 4px 4px 0 rgba(0,0,0,0.9)';
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
      SHARE YOUR WIN ON X!
    </button>
  );
};

// ─── Helper ───────────────────────────────────────────────────────────────────

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

// Draft environment: allow re-spinning (bypass 24h cooldown)
const isDraftEnv = import.meta.env.VITE_ENV !== 'LIVE';

// ─── Main LuckyDraw Component ─────────────────────────────────────────────────

export const LuckyDraw: React.FC = () => {
  const { user, login, logout } = useAuth() as import('@/components/types').AuthContextType;
  const sectionRef = useScrollAnimation<HTMLElement>(0.05);

  const [drawState, setDrawState] = useState<DrawState>('idle');
  const [drawResult, setDrawResult] = useState<DrawResult | null>(null);
  const [nextDrawTs, setNextDrawTs] = useState<number | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const { hours, minutes, seconds } = useCountdown(nextDrawTs);

  // Realtime prizes
  const { data: prizesData } = useRealtimeData<LuckyDrawPrizesResponse[]>(
    subscribeManyLuckyDrawPrizes,
    true,
  );
  const prizes = prizesData ?? [];

  // Realtime campaign (for startAt/endAt date gating)
  const { data: campaign } = useRealtimeData<LuckyDrawCampaignsResponse | null>(
    subscribeLuckyDrawCampaigns,
    true,
    'default',
  );

  // Derived campaign state: check both isActive flag and date window
  const now = Math.floor(Date.now() / 1000);
  const campaignActive = (() => {
    if (!campaign) return prizes.some(p => p.campaignActive); // fall back to prize flag if no campaign doc
    if (!campaign.isActive) return false;
    if (campaign.startAt && now < campaign.startAt) return false;
    if (campaign.endAt && now > campaign.endAt) return false;
    return true;
  })();
  const allSoldOut = prizes.length > 0 && prizes.every(p => p.remainingCount <= 0);

  // Determine if we're "coming soon" (campaign exists but hasn't started yet)
  const isCamingSoon = campaign?.isActive && campaign.startAt && now < campaign.startAt;

  // Build dynamic reel prizes from live data, falling back to defaults before load
  const reelPrizes = useMemo<ReelPrize[]>(() => {
    if (prizes.length === 0) return DEFAULT_REEL_PRIZES;
    return prizes.map((p, i) => ({
      key: p.prizeType,
      emoji: PRIZE_EMOJI[p.prizeType] ?? '🎁',
      label: p.label,
      color: PRIZE_COLOR[p.prizeType] ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      imageUrl: p.imageUrl,
    }));
  }, [prizes]);

  // ─── Slot machine animation state ─────────────────────────────────────────
  const [spinKey, setSpinKey] = useState(0);
  const [reelStates, setReelStates] = useState([
    { targetIndex: 0, spinning: false, stopped: false },
    { targetIndex: 0, spinning: false, stopped: false },
    { targetIndex: 0, spinning: false, stopped: false },
  ]);
  const [showWinOverlay, setShowWinOverlay] = useState(false);
  const [showLoseOverlay, setShowLoseOverlay] = useState(false);

  // Track draws today count (0 or 1)
  const [drewToday, setDrewToday] = useState(0);

  // ─── localStorage cooldown check ──────────────────────────────────────────
  useEffect(() => {
    // On draft environment, always allow spinning — skip the cooldown check
    if (isDraftEnv) {
      setDrewToday(0);
      setNextDrawTs(null);
      return;
    }
    const lastTs = localStorage.getItem(LS_LAST_DRAW_TS);
    if (!lastTs) {
      setDrewToday(0);
      setNextDrawTs(null);
      return;
    }
    const lastSec = parseInt(lastTs, 10);
    const nowSec = Date.now() / 1000;
    if (nowSec - lastSec < 86400) {
      const next = lastSec + 86400;
      setNextDrawTs(next);
      setDrewToday(1);
      if (drawState === 'idle') setDrawState('already_drew');
    } else {
      setDrewToday(0);
      setNextDrawTs(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Restore win state from localStorage ──────────────────────────────────
  useEffect(() => {
    // On draft environment, skip restoring win state so users can spin again
    if (isDraftEnv) return;
    const savedEntryId = localStorage.getItem(LS_WIN_ENTRY_ID);
    const savedPrizeType = localStorage.getItem(LS_WIN_PRIZE_TYPE);
    if (savedEntryId && savedPrizeType && drawState === 'idle') {
      const lastTs = localStorage.getItem(LS_LAST_DRAW_TS);
      if (lastTs) {
        const lastSec = parseInt(lastTs, 10);
        const nowSec = Date.now() / 1000;
        if (nowSec - lastSec < 86400) {
          setDrawState('won');
          setDrawResult({
            won: true,
            prizeType: savedPrizeType,
            entryId: savedEntryId,
          });
        }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Spin animation sequence ───────────────────────────────────────────────
  const runSpinAnimation = useCallback(
    (result: DrawResult, currentReelPrizes: ReelPrize[]) => {
      // Determine target reel indices
      let targets: [number, number, number];
      if (result.won) {
        const idx = currentReelPrizes.findIndex(p => p.key === result.prizeType);
        const t = idx >= 0 ? idx : 0;
        targets = [t, t, t];
      } else {
        // Deliberately mismatched — use indices 0, 1, 2 (clamped to prize count)
        const maxIdx = Math.max(0, currentReelPrizes.length - 1);
        targets = [0, Math.min(1, maxIdx), Math.min(2, maxIdx)];
      }

      const newSpinKey = spinKey + 1;
      setSpinKey(newSpinKey);
      setShowWinOverlay(false);
      setShowLoseOverlay(false);

      // Start all 3 spinning
      setReelStates([
        { targetIndex: targets[0], spinning: true, stopped: false },
        { targetIndex: targets[1], spinning: true, stopped: false },
        { targetIndex: targets[2], spinning: true, stopped: false },
      ]);

      // Stop reel 1 at ~1.5s
      setTimeout(() => {
        playClick();
        setReelStates(prev => [
          { ...prev[0], spinning: false, stopped: true },
          prev[1],
          prev[2],
        ]);
      }, 1500);

      // Stop reel 2 at ~2.5s
      setTimeout(() => {
        playClick();
        setReelStates(prev => [
          prev[0],
          { ...prev[1], spinning: false, stopped: true },
          prev[2],
        ]);
      }, 2500);

      // Stop reel 3 at ~3.5s
      setTimeout(() => {
        playClick();
        setReelStates(prev => [
          prev[0],
          prev[1],
          { ...prev[2], spinning: false, stopped: true },
        ]);

        // Show result overlay at ~4.1s (after reel settles)
        setTimeout(() => {
          if (result.won) {
            playBoing();
            setShowWinOverlay(true);
          } else {
            playPop();
            setShowLoseOverlay(true);
            // Transition to already_drew after 2.5s
            setTimeout(() => {
              setShowLoseOverlay(false);
              setDrawState('already_drew');
            }, 2500);
          }
        }, 600);
      }, 3500);
    },
    [spinKey],
  );

  // ─── Handle Draw ───────────────────────────────────────────────────────────
  const handleDraw = useCallback(async () => {
    playClick();
    setDrawState('drawing');
    setShowWinOverlay(false);
    setShowLoseOverlay(false);

    let token: string | null | undefined;
    try {
      token = await getIdToken();
    } catch {
      toast.error('Session expired. Please reconnect your wallet.');
      setDrawState('idle');
      return;
    }

    try {
      if (!token || !user?.address) {
        toast.error('Session expired. Please reconnect your wallet.');
        setDrawState('idle');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, user.address);
      const result = await authApi.post<DrawResult & { status?: string }>('/api/lucky-draw', {
        twitterHandle: '',
      });

      // Handle max wins reached (server returns 200 with status field)
      if (result.status === 'max_wins_reached') {
        setDrawState('max_wins_reached');
        setReelStates([
          { targetIndex: 0, spinning: false, stopped: false },
          { targetIndex: 0, spinning: false, stopped: false },
          { targetIndex: 0, spinning: false, stopped: false },
        ]);
        return;
      }

      // Record draw timestamp in localStorage (skipped on draft so users can spin again)
      if (!isDraftEnv) {
        const nowSec = Math.floor(Date.now() / 1000);
        localStorage.setItem(LS_LAST_DRAW_TS, String(nowSec));
        setNextDrawTs(nowSec + 86400);
        setDrewToday(1);
      }
      // Persist win state for page reload
      if (result.won) {
        if (result.entryId) localStorage.setItem(LS_WIN_ENTRY_ID, result.entryId);
        localStorage.setItem(LS_WIN_PRIZE_TYPE, result.prizeType);
      }

      setDrawResult(result);

      // Run animation with known result
      runSpinAnimation(result, reelPrizes);

      if (result.won) {
        setDrawState('won');
        toast.success(`You won: ${result.prizeType}!`);
      }
      // Lost state transition happens in animation timeout above
    } catch (err: any) {
      const msg = err?.message ?? 'Draw failed';
      if (msg.includes('Already drew today') || msg.includes('Next draw available')) {
        setDrawState('already_drew');
        const match = msg.match(/(\d+)\./);
        if (match) setNextDrawTs(parseInt(match[1], 10));
      } else {
        toast.error(msg);
        setDrawState('idle');
      }
      // Reset reels on error
      setReelStates([
        { targetIndex: 0, spinning: false, stopped: false },
        { targetIndex: 0, spinning: false, stopped: false },
        { targetIndex: 0, spinning: false, stopped: false },
      ]);
    }
  }, [user, runSpinAnimation, reelPrizes]);

  const handleConnectWallet = useCallback(() => {
    playClick();
    login();
  }, [login]);

  // ─── Effective state ───────────────────────────────────────────────────────
  const effectiveState: DrawState = (() => {
    if (!campaignActive || allSoldOut) return 'campaign_ended';
    return drawState;
  })();

  // ─── 3-step state machine ──────────────────────────────────────────────────
  const walletConnected = !!user?.address;
  const isDrawing = effectiveState === 'drawing' || reelStates.some(r => r.spinning && !r.stopped);

  // PLAY button is only enabled when wallet is connected
  // On draft environment, won/already_drew states do not block re-spinning
  const spinDisabled =
    !walletConnected ||
    isDrawing ||
    (!isDraftEnv && effectiveState === 'won') ||
    (!isDraftEnv && effectiveState === 'already_drew') ||
    effectiveState === 'campaign_ended' ||
    effectiveState === 'max_wins_reached';

  const spinLabel = isDrawing
    ? 'SPINNING...'
    : effectiveState === 'campaign_ended'
    ? (isCamingSoon ? 'COMING SOON' : 'DRAW ENDED')
    : !isDraftEnv && effectiveState === 'max_wins_reached'
    ? 'MAX WINS REACHED'
    : !isDraftEnv && effectiveState === 'already_drew'
    ? 'COME BACK LATER'
    : !walletConnected
    ? 'PLAY'
    : 'SPIN!';

  const wonPrize = drawResult?.won ? prizes.find(p => p.prizeType === drawResult.prizeType) : undefined;
  const wonPrizeLabel = wonPrize?.label ?? (drawResult?.prizeType ?? '');
  const wonPrizeImageUrl = wonPrize?.imageUrl;

  // Map prizeKey -> imageUrl for reel tiles (built from dynamic reelPrizes)
  const prizeImageMap: Record<string, string> = {};
  reelPrizes.forEach(p => { if (p.imageUrl) prizeImageMap[p.key] = p.imageUrl; });

  // Entry ID preserved for reference (not used in claim flow anymore)
  // const winEntryId = drawResult?.entryId ?? localStorage.getItem(LS_WIN_ENTRY_ID) ?? '';

  return (
    <section
      ref={sectionRef}
      className="relative py-16 scroll-fade-in"
      style={{ overflow: 'hidden' }}
    >
      {/* Background halftone */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(124,252,0,1) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          opacity: 0.018,
          pointerEvents: 'none',
        }}
      />

      {/* Section header */}
      <div className="container relative z-10">
        <div className="flex flex-col items-center mb-10">
          <div
            style={{
              display: 'inline-block',
              background: '#7CFC00',
              color: '#080d1f',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.16em',
              padding: '2px 14px',
              border: '3px solid rgba(0,0,0,0.75)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
              transform: 'rotate(-1.2deg)',
              marginBottom: '12px',
              userSelect: 'none',
            }}
          >
            LIMITED TIME CAMPAIGN
          </div>
        </div>

        {/* ── Main layout: Centered machine only ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '0 8px',
          }}
        >
          <SlotMachine
            reelStates={reelStates}
            onSpin={handleDraw}
            spinDisabled={spinDisabled}
            spinKey={spinKey}
            spinLabel={spinLabel}
            effectiveState={effectiveState}
            isCamingSoon={!!isCamingSoon}
            campaignStartAt={campaign?.startAt}
            hours={hours}
            minutes={minutes}
            seconds={seconds}
            showWinOverlay={showWinOverlay}
            showLoseOverlay={showLoseOverlay}
            winPrizeType={drawResult?.prizeType ?? ''}
            winPrizeLabel={wonPrizeLabel}
            winPrizeImageUrl={wonPrizeImageUrl}
            walletConnected={walletConnected}
            onConnectWallet={handleConnectWallet}
            onDisconnectWallet={logout}
            prizeImageMap={prizeImageMap}
            reelPrizes={reelPrizes}
            onOpenInfo={() => setInfoOpen(true)}
          />
        </div>

        {/* Info popup */}
        <InfoPopup
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          drawsToday={drewToday}
          hours={hours}
          minutes={minutes}
          seconds={seconds}
          prizes={prizes}
          wonPrizeType={drawResult?.won ? drawResult.prizeType : undefined}
          walletAddress={user?.address ?? undefined}
        />

        {/* Won state — share on X flow below machine */}
        {effectiveState === 'won' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '24px',
            }}
          >
            <div style={{ width: '100%', maxWidth: '460px' }}>
              <WinShareFlow
                prizeType={drawResult?.prizeType ?? ''}
                prizeName={wonPrizeLabel || (drawResult?.prizeType ?? 'Prize')}
              />
            </div>
            {nextDrawTs && (
              <div style={{ marginTop: '20px' }}>
                <CountdownDisplay hours={hours} minutes={minutes} seconds={seconds} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes popup-bg-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes popup-card-in {
          0% { transform: scale(0.88) translateY(12px); opacity: 0; }
          70% { transform: scale(1.02) translateY(-2px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes sm-win-flash {
          0% { background: rgba(124,252,0,0.35); }
          100% { background: radial-gradient(circle at 50% 50%, rgba(124,252,0,0.15) 0%, rgba(8,13,31,0.97) 70%); }
        }
        @keyframes sm-jackpot-in {
          0% { transform: scale(0.4) rotate(-8deg); opacity: 0; }
          70% { transform: scale(1.12) rotate(2deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes sm-prize-drop {
          0% { transform: scale(0) translateY(-20px); opacity: 0; }
          70% { transform: scale(1.2) translateY(4px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes sm-label-up {
          from { transform: translateY(16px) rotate(-2deg); opacity: 0; }
          to { transform: translateY(0) rotate(-1deg); opacity: 1; }
        }
        @keyframes sm-lose-shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-10px) rotate(-1.5deg); }
          35% { transform: translateX(10px) rotate(1.5deg); }
          55% { transform: translateX(-6px) rotate(-0.8deg); }
          75% { transform: translateX(6px); }
        }
        @keyframes sm-confetti-0 {
          0% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + 80px), calc(-50% - 90px)) scale(0.3) rotate(360deg); opacity: 0; }
        }
        @keyframes sm-confetti-1 {
          0% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% - 100px), calc(-50% - 60px)) scale(0.2) rotate(-270deg); opacity: 0; }
        }
        @keyframes sm-confetti-2 {
          0% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + 50px), calc(-50% + 110px)) scale(0.4) rotate(180deg); opacity: 0; }
        }
        @keyframes sm-confetti-3 {
          0% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% - 70px), calc(-50% + 80px)) scale(0.3) rotate(-360deg); opacity: 0; }
        }

        /* ── Neon arcade cabinet animations ── */

        /* Outer border color cycle: green → red → yellow → green */
        @keyframes neon-border-cycle {
          0%   { border-color: #7CFC00; box-shadow: 0 0 0 4px rgba(124,252,0,0.18), 0 0 60px rgba(124,252,0,0.22), 0 0 120px rgba(124,252,0,0.08), 8px 8px 0 rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04); }
          33%  { border-color: #E63946; box-shadow: 0 0 0 4px rgba(230,57,70,0.22), 0 0 60px rgba(230,57,70,0.28), 0 0 120px rgba(230,57,70,0.1), 8px 8px 0 rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04); }
          66%  { border-color: #FFD700; box-shadow: 0 0 0 4px rgba(255,215,0,0.22), 0 0 60px rgba(255,215,0,0.28), 0 0 120px rgba(255,215,0,0.1), 8px 8px 0 rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04); }
          100% { border-color: #7CFC00; box-shadow: 0 0 0 4px rgba(124,252,0,0.18), 0 0 60px rgba(124,252,0,0.22), 0 0 120px rgba(124,252,0,0.08), 8px 8px 0 rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04); }
        }

        /* LED strip pulse on left/right sides */
        @keyframes led-strip-left {
          0%   { opacity: 1;   background-position: 0% 0%; }
          50%  { opacity: 0.5; background-position: 0% 100%; }
          100% { opacity: 1;   background-position: 0% 0%; }
        }
        @keyframes led-strip-right {
          0%   { opacity: 0.5; background-position: 0% 100%; }
          50%  { opacity: 1;   background-position: 0% 0%; }
          100% { opacity: 0.5; background-position: 0% 100%; }
        }

        /* Corner neon diamond flash */
        @keyframes neon-corner-pulse {
          0%, 100% { opacity: 1;   filter: drop-shadow(0 0 10px rgba(124,252,0,1)) drop-shadow(0 0 20px rgba(124,252,0,0.6)); transform: scale(1) rotate(0deg); }
          25%       { opacity: 0.7; filter: drop-shadow(0 0 8px rgba(230,57,70,0.9)) drop-shadow(0 0 18px rgba(230,57,70,0.5)); transform: scale(1.15) rotate(15deg); }
          50%       { opacity: 1;   filter: drop-shadow(0 0 10px rgba(255,215,0,1)) drop-shadow(0 0 20px rgba(255,215,0,0.6)); transform: scale(1) rotate(0deg); }
          75%       { opacity: 0.8; filter: drop-shadow(0 0 8px rgba(124,252,0,0.9)) drop-shadow(0 0 18px rgba(124,252,0,0.5)); transform: scale(1.1) rotate(-10deg); }
        }

        /* Top title glow pulse */
        @keyframes title-neon-pulse {
          0%, 100% { text-shadow: 0 0 20px rgba(0,191,255,0.9), 0 0 50px rgba(0,191,255,0.5), 0 0 80px rgba(0,191,255,0.2), 3px 3px 0 rgba(0,0,0,0.9); }
          50%       { text-shadow: 0 0 30px rgba(0,191,255,1),   0 0 70px rgba(0,191,255,0.7), 0 0 110px rgba(0,191,255,0.35), 3px 3px 0 rgba(0,0,0,0.9); }
        }

        /* Reel center row highlight flicker */
        @keyframes reel-highlight-flicker {
          0%, 90%, 100% { border-color: rgba(124,252,0,0.5); box-shadow: inset 0 0 16px rgba(124,252,0,0.12); }
          93%            { border-color: rgba(255,215,0,0.6);  box-shadow: inset 0 0 16px rgba(255,215,0,0.15); }
          96%            { border-color: rgba(124,252,0,0.4);  box-shadow: inset 0 0 12px rgba(124,252,0,0.08); }
        }

        /* Cabinet outer shell class */
        .arcade-cabinet {
          border-color: #7CFC00;
          box-shadow: 0 0 0 4px rgba(124,252,0,0.18), 0 0 60px rgba(124,252,0,0.22), 0 0 120px rgba(124,252,0,0.08), 8px 8px 0 rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04);
        }

        /* Mobile responsive cabinet */
        @media (max-width: 639px) {
          .arcade-cabinet {
            border-width: 4px !important;
            border-radius: 10px !important;
            padding-bottom: 18px !important;
          }
          .arcade-cabinet-inner {
            padding: 10px 14px 0 !important;
          }
          .cabinet-title-neon {
            font-size: 1.7rem !important;
          }
          .arcade-reel {
            width: 82px !important;
          }
          .arcade-spin-btn {
            padding: 8px 28px !important;
            font-size: 1.4rem !important;
          }
        }

        /* Side LED strips */
        .led-strip-left {
          background: linear-gradient(
            180deg,
            #7CFC00, #E63946, #FFD700, #7CFC00, #E63946, #7CFC00
          );
          background-size: 100% 200%;
          animation: led-strip-left 2s ease-in-out infinite;
        }
        .led-strip-right {
          background: linear-gradient(
            180deg,
            #E63946, #FFD700, #7CFC00, #E63946, #FFD700, #E63946
          );
          background-size: 100% 200%;
          animation: led-strip-right 2s ease-in-out infinite;
        }

        /* Neon corner diamonds */
        .neon-corner {
          animation: neon-corner-pulse 3.2s ease-in-out infinite;
        }
        .neon-corner:nth-child(2) { animation-delay: 0.8s; }
        .neon-corner:nth-child(3) { animation-delay: 1.6s; }
        .neon-corner:nth-child(4) { animation-delay: 2.4s; }

        /* Cabinet title neon flicker */
        .cabinet-title-neon {
          animation: title-neon-pulse 1.8s ease-in-out infinite;
        }

        /* Reel frame center line */
        .reel-center-highlight {
          animation: reel-highlight-flicker 5s ease-in-out infinite;
        }

        /* Share button pulse */
        @keyframes share-btn-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(255,255,255,0.08), 4px 4px 0 rgba(0,0,0,0.9); }
          50%       { box-shadow: 0 0 35px rgba(255,255,255,0.18), 4px 4px 0 rgba(0,0,0,0.9); }
        }
      `}</style>
    </section>
  );
};

export default LuckyDraw;
